import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { X, Truck, DownloadSimple } from '@phosphor-icons/react';

/**
 * CMRModal — Generator voor een Internationale Vrachtbrief (CMR).
 * Vertrekt van een bestaande leverbon of factuur → vraagt de transport-specifieke velden
 * die niet in de leverbon staan (vervoerder, kenteken, laad/los-datum, bepalingen) →
 * opent de PDF met 4 gekleurde originelen (rood/blauw/groen/zwart).
 */
export default function CMRModal({ docId, docType = 'leverbon', klantNaam = '', onClose }) {
  const [form, setForm] = useState({
    afzender_naam: '', afzender_adres: '', afzender_btw: '', afzender_tel: '',
    geadresseerde_naam: '', geadresseerde_adres: '', geadresseerde_btw: '', geadresseerde_tel: '',
    vervoerder: '', vervoerder_adres: '', kenteken: '', licentie: '',
    laad_datum: new Date().toISOString().slice(0, 10),
    los_datum: new Date().toISOString().slice(0, 10),
    laadplaats: '', losplaats: '',
    bepalingen: '', incoterm: 'DAP', vrachtkosten: '',
    waarde_goederen: '',
    betaalt_afzender: true,
    totaal_bruto: '', totaal_netto: '', totaal_vol: '',
    adr: 'Niet van toepassing', douane: 'Intracomm. levering (EU)', rembours: 'Niet van toepassing',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [vervoerders, setVervoerders] = useState([]);
  const [sjablonen, setSjablonen] = useState([]);
  const [saveSjabBezig, setSaveSjabBezig] = useState(false);

  // Laad opgeslagen vervoerders + sjablonen
  useEffect(() => {
    API.get('/vervoerders').then(r => setVervoerders((r.data || []).filter(v => v.actief !== false))).catch(() => setVervoerders([]));
    API.get('/cmr-sjablonen').then(r => setSjablonen(r.data || [])).catch(() => setSjablonen([]));
  }, []);

  // Auto-fill afzender + geadresseerde vanuit bedrijfsinstellingen en doc/klant
  useEffect(() => {
    API.get('/instellingen').then(r => {
      const i = r.data || {};
      const adres = [i.adres, [i.postcode, i.plaats].filter(Boolean).join(' '), i.land || 'België'].filter(Boolean).join(', ');
      setForm(p => ({
        ...p,
        afzender_naam: p.afzender_naam || i.bedrijf || i.naam || '',
        afzender_adres: p.afzender_adres || adres,
        afzender_btw: p.afzender_btw || i.btwnr || i.btw || '',
        afzender_tel: p.afzender_tel || i.tel || i.telefoon || '',
      }));
    }).catch(() => {});
  }, []);

  const kiesSjabloon = (sid) => {
    if (!sid) return;
    const s = sjablonen.find(x => x.id === sid);
    if (!s || !s.velden) return;
    setForm(p => ({ ...p, ...s.velden }));
  };

  const bewaarSjabloon = async () => {
    const naam = window.prompt('Naam van het sjabloon (bv. "Wekelijkse rit Antwerpen-Rotterdam"):');
    if (!naam) return;
    setSaveSjabBezig(true);
    try {
      // Bewaar alle invoervelden behalve datum-velden (die zijn rit-specifiek)
      const { laad_datum, los_datum, ...velden } = form;
      const r = await API.post('/cmr-sjablonen', { naam: naam.trim(), velden });
      setSjablonen(prev => [...prev, r.data]);
      setErr('');
      window.alert(`Sjabloon '${r.data.naam}' bewaard. U kunt het volgende keer kiezen uit de dropdown.`);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Sjabloon bewaren mislukt');
    }
    setSaveSjabBezig(false);
  };

  const verwijderSjabloon = async (sid, naam) => {
    if (!window.confirm(`Sjabloon "${naam}" verwijderen?`)) return;
    try {
      await API.delete(`/cmr-sjablonen/${sid}`);
      setSjablonen(prev => prev.filter(s => s.id !== sid));
    } catch (e) {
      setErr(e.response?.data?.detail || 'Verwijderen mislukt');
    }
  };

  // Vul form met geselecteerde vervoerder
  const kiesVervoerder = (vid) => {
    if (!vid) return;
    const v = vervoerders.find(x => x.id === vid);
    if (!v) return;
    const adresLijnen = [v.adres, [v.postcode, v.stad].filter(Boolean).join(' '), v.land].filter(Boolean).join(', ');
    setForm(p => ({
      ...p,
      vervoerder: v.naam || '',
      vervoerder_adres: adresLijnen,
      kenteken: v.standaard_kenteken || p.kenteken,
      licentie: v.vervoerderslicentie || p.licentie,
    }));
  };

  // Auto-fill uit de onderliggende leverbon + klantgegevens (geadresseerde)
  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await API.get(`/documenten/${docId}`);
        setForm(p => ({
          ...p,
          laad_datum: d.datum || p.laad_datum,
          los_datum: d.leverdatum || d.datum || p.los_datum,
          waarde_goederen: d.totaal ?? p.waarde_goederen,
        }));
        if (d.klant_id) {
          try {
            const { data: customers } = await API.get('/klanten');
            const k = customers.find(customer => customer.id === d.klant_id);
            if (!k) return;
            const naam = k.bedrijf || `${k.voorn || ''} ${k.achtern || ''}`.trim();
            const adres = [
              [k.straat || k.adres, k.huisnr || k.nr].filter(Boolean).join(' '),
              [k.postcode, k.gemeente || k.stad].filter(Boolean).join(' '),
              k.land,
            ].filter(Boolean).join(', ');
            setForm(p => ({
              ...p,
              geadresseerde_naam: p.geadresseerde_naam || naam,
              geadresseerde_adres: p.geadresseerde_adres || adres,
              geadresseerde_btw: p.geadresseerde_btw || k.btw || k.btwnr || '',
              geadresseerde_tel: p.geadresseerde_tel || k.gsm || k.tel || '',
            }));
          } catch { /* niet kritiek */ }
        }
      } catch { /* niet kritiek */ }
    })();
  }, [docId]);

  const update = (k) => (e) => {
    const v = e.target?.type === 'checkbox' ? e.target.checked : (e.target?.value ?? e);
    setForm(p => ({ ...p, [k]: v }));
  };

  const genereer = async () => {
    setBusy(true); setErr('');
    try {
      const payload = { ...form };
      // Getallen parsen
      ['waarde_goederen', 'totaal_bruto', 'totaal_netto', 'totaal_vol'].forEach(k => {
        if (payload[k] === '' || payload[k] === null) delete payload[k];
        else payload[k] = parseFloat(payload[k]);
      });
      const res = await API.post(`/documenten/${docId}/cmr`, payload, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      onClose?.();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'CMR-generatie mislukt');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="cmr-modal">
      <div className="w-full max-w-3xl bg-white shadow-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gradient-to-r from-[#DC2626] via-[#2563EB] to-[#059669] text-white">
          <div>
            <h3 className="text-sm font-bold inline-flex items-center gap-2"><Truck size={18} weight="bold" /> CMR genereren — Internationale Vrachtbrief</h3>
            <p className="text-[11px] opacity-90 mt-0.5">{docId}{klantNaam && ` · ${klantNaam}`} · 4 originelen worden afgedrukt (rood/blauw/groen/zwart)</p>
          </div>
          <button onClick={onClose} className="btn-ghost p-1 text-white" data-testid="cmr-close"><X size={18} /></button>
        </div>

        {err && <div className="px-5 py-2 bg-red-50 border-b border-red-200 text-xs text-red-700 font-bold">{err}</div>}

        {/* Sjablonen-bar: snelkeuze + bewaren */}
        <div className="px-5 py-2 bg-amber-50 border-b border-amber-200 flex items-center gap-2 flex-wrap" data-testid="cmr-sjablonen-bar">
          <span className="text-[10px] font-bold text-amber-900 uppercase tracking-wider shrink-0">Sjabloon:</span>
          {sjablonen.length > 0 ? (
            <select
              className="field-input text-xs flex-1 min-w-[200px]"
              onChange={e => { kiesSjabloon(e.target.value); e.target.value=''; }}
              defaultValue=""
              data-testid="cmr-sjabloon-select"
            >
              <option value="">— Kies opgeslagen sjabloon (auto-invullen) —</option>
              {sjablonen.map(s => <option key={s.id} value={s.id}>{s.naam}</option>)}
            </select>
          ) : (
            <span className="text-[11px] text-gray-500 italic flex-1">Nog geen sjablonen. Vul de velden hieronder in en klik op "Bewaar als sjabloon".</span>
          )}
          <button
            type="button"
            onClick={bewaarSjabloon}
            disabled={saveSjabBezig}
            className="btn btn-sm bg-amber-600 text-white font-bold hover:bg-amber-700 inline-flex items-center gap-1 text-[11px]"
            data-testid="cmr-bewaar-sjabloon"
          >
            {saveSjabBezig ? 'Bezig...' : '💾 Bewaar als sjabloon'}
          </button>
          {sjablonen.length > 0 && (
            <details className="text-[10px] text-gray-600 ml-auto">
              <summary className="cursor-pointer">Beheer</summary>
              <div className="absolute right-5 mt-1 bg-white border border-gray-300 shadow-lg p-2 z-10 max-h-48 overflow-auto">
                {sjablonen.map(s => (
                  <div key={s.id} className="flex items-center justify-between gap-2 py-1 text-xs whitespace-nowrap">
                    <span>{s.naam}</span>
                    <button onClick={() => verwijderSjabloon(s.id, s.naam)} className="text-red-600 hover:text-red-800 font-bold">Verwijder</button>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <section>
            <h4 className="text-[11px] font-bold uppercase text-gray-500 mb-2 tracking-wider">Afzender (vak 1) <span className="text-gray-400 normal-case text-[9px]">· automatisch ingevuld vanuit bedrijfsinstellingen — wijzig enkel bij uitzondering</span></h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input className="field-input text-xs" placeholder="Bedrijfsnaam" value={form.afzender_naam} onChange={update('afzender_naam')} data-testid="cmr-afzender-naam" />
              <input className="field-input text-xs" placeholder="BTW-nummer" value={form.afzender_btw} onChange={update('afzender_btw')} data-testid="cmr-afzender-btw" />
              <input className="field-input text-xs sm:col-span-2" placeholder="Volledig adres (straat, nr, postcode, stad, land)" value={form.afzender_adres} onChange={update('afzender_adres')} data-testid="cmr-afzender-adres" />
              <input className="field-input text-xs sm:col-span-2" placeholder="Telefoon" value={form.afzender_tel} onChange={update('afzender_tel')} data-testid="cmr-afzender-tel" />
            </div>
          </section>

          <section>
            <h4 className="text-[11px] font-bold uppercase text-gray-500 mb-2 tracking-wider">Geadresseerde (vak 2) <span className="text-gray-400 normal-case text-[9px]">· automatisch ingevuld vanuit klantgegevens — wijzig enkel bij uitzondering (bv. doorlevering)</span></h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input className="field-input text-xs" placeholder="Naam ontvanger" value={form.geadresseerde_naam} onChange={update('geadresseerde_naam')} data-testid="cmr-geadr-naam" />
              <input className="field-input text-xs" placeholder="BTW-nummer" value={form.geadresseerde_btw} onChange={update('geadresseerde_btw')} data-testid="cmr-geadr-btw" />
              <input className="field-input text-xs sm:col-span-2" placeholder="Volledig adres (straat, nr, postcode, stad, land)" value={form.geadresseerde_adres} onChange={update('geadresseerde_adres')} data-testid="cmr-geadr-adres" />
              <input className="field-input text-xs sm:col-span-2" placeholder="Telefoon" value={form.geadresseerde_tel} onChange={update('geadresseerde_tel')} data-testid="cmr-geadr-tel" />
            </div>
          </section>

          <section>
            <h4 className="text-[11px] font-bold uppercase text-gray-500 mb-2 tracking-wider">Vervoerder (vak 5)</h4>
            {vervoerders.length > 0 && (
              <div className="mb-2 flex items-center gap-2">
                <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider shrink-0">Snelkeuze:</label>
                <select
                  className="field-input text-xs flex-1"
                  onChange={e => kiesVervoerder(e.target.value)}
                  defaultValue=""
                  data-testid="cmr-vervoerder-select"
                >
                  <option value="">— Kies opgeslagen vervoerder (auto-invullen) —</option>
                  {vervoerders.map(v => (
                    <option key={v.id} value={v.id}>
                      {v.naam}{v.stad ? ` · ${v.stad}` : ''}{v.standaard_kenteken ? ` · ${v.standaard_kenteken}` : ''}
                    </option>
                  ))}
                </select>
                <a href="/vervoerders" target="_blank" rel="noreferrer" className="text-[10px] text-[#002FA7] font-bold hover:underline shrink-0" title="Vervoerders beheren">+ Beheren</a>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input className="field-input text-xs" placeholder="Naam vervoerder (bv. Trans Europa NV)" value={form.vervoerder} onChange={update('vervoerder')} data-testid="cmr-vervoerder" />
              <input className="field-input text-xs" placeholder="Kenteken (bv. 1-ABC-234)" value={form.kenteken} onChange={update('kenteken')} data-testid="cmr-kenteken" />
              <input className="field-input text-xs sm:col-span-2" placeholder="Adres vervoerder" value={form.vervoerder_adres} onChange={update('vervoerder_adres')} data-testid="cmr-vervoerder-adres" />
              <input className="field-input text-xs sm:col-span-2" placeholder="Licentie / vervoersvergunning (optioneel)" value={form.licentie} onChange={update('licentie')} />
            </div>
          </section>

          <section>
            <h4 className="text-[11px] font-bold uppercase text-gray-500 mb-2 tracking-wider">Laad- en losplaats (vak 3, 4)</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input className="field-input text-xs" placeholder="Laadplaats (standaard = uw bedrijfsadres)" value={form.laadplaats} onChange={update('laadplaats')} />
              <input className="field-input text-xs" placeholder="Losplaats (standaard = klantadres)" value={form.losplaats} onChange={update('losplaats')} />
              <div><label className="form-label text-[10px]">Laaddatum</label><input type="date" className="field-input text-xs" value={form.laad_datum} onChange={update('laad_datum')} /></div>
              <div><label className="form-label text-[10px]">Losdatum</label><input type="date" className="field-input text-xs" value={form.los_datum} onChange={update('los_datum')} /></div>
            </div>
          </section>

          <section>
            <h4 className="text-[11px] font-bold uppercase text-gray-500 mb-2 tracking-wider">Gewichten en volume (vak 7) <span className="text-gray-400 normal-case text-[9px]">(laat leeg om automatisch berekend te laten)</span></h4>
            <div className="grid grid-cols-3 gap-2">
              <div><label className="form-label text-[10px]">Bruto (kg)</label><input type="number" step="0.1" className="field-input text-xs" value={form.totaal_bruto} onChange={update('totaal_bruto')} placeholder="auto" data-testid="cmr-bruto" /></div>
              <div><label className="form-label text-[10px]">Netto (kg)</label><input type="number" step="0.1" className="field-input text-xs" value={form.totaal_netto} onChange={update('totaal_netto')} placeholder="auto" /></div>
              <div><label className="form-label text-[10px]">Volume (m³)</label><input type="number" step="0.01" className="field-input text-xs" value={form.totaal_vol} onChange={update('totaal_vol')} placeholder="auto" /></div>
            </div>
            <p className="text-[10px] text-gray-500 mt-1 italic">Tip: zet het <strong>gewicht_kg</strong>-veld per artikel in de Artikels-module om automatisch gewichten te laten invullen.</p>
          </section>

          <section>
            <h4 className="text-[11px] font-bold uppercase text-gray-500 mb-2 tracking-wider">Transportvoorwaarden (vak 10, 12, 13)</h4>
            <textarea className="field-input text-xs" rows="2" placeholder="Bijzondere bepalingen (bv. Niet stapelbaar · Houd droog · Breekbaar)" value={form.bepalingen} onChange={update('bepalingen')} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
              <input className="field-input text-xs" placeholder="Incoterm (bv. DAP, CIF, EXW)" value={form.incoterm} onChange={update('incoterm')} />
              <input className="field-input text-xs" placeholder="Vrachtkosten (bv. € 620,00)" value={form.vrachtkosten} onChange={update('vrachtkosten')} />
              <input type="number" step="0.01" className="field-input text-xs" placeholder="Waarde goederen (€)" value={form.waarde_goederen} onChange={update('waarde_goederen')} />
            </div>
            <label className="mt-2 inline-flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={form.betaalt_afzender} onChange={update('betaalt_afzender')} className="w-4 h-4" />
              <span>Afzender betaalt vrachtkosten (anders: geadresseerde)</span>
            </label>
          </section>

          <section>
            <h4 className="text-[11px] font-bold uppercase text-gray-500 mb-2 tracking-wider">Extra info (vak 8, 9, 11)</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input className="field-input text-xs" placeholder="Gevaarlijke stoffen (ADR/UN)" value={form.adr} onChange={update('adr')} />
              <input className="field-input text-xs" placeholder="Douane en accijnzen" value={form.douane} onChange={update('douane')} />
              <input className="field-input text-xs" placeholder="Te betalen bij aflevering (rembours)" value={form.rembours} onChange={update('rembours')} />
            </div>
          </section>
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50">
          <p className="text-[10px] text-gray-500 italic">PDF opent in nieuw venster · afdrukken op 4 A4-bladen (één per kleur)</p>
          <div className="flex gap-2">
            <button className="btn btn-secondary btn-sm" onClick={onClose}>Annuleren</button>
            <button className="btn btn-primary btn-sm inline-flex items-center gap-1.5" onClick={genereer} disabled={busy} data-testid="cmr-genereer">
              <DownloadSimple size={14} weight="bold" /> {busy ? 'Bezig...' : 'CMR genereren (4 originelen)'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
